import { Outlet, createFileRoute, useParams } from "@tanstack/react-router";
import { useMe } from "@core/auth";
import { AppShell } from "@core/components";
import { RoundOperationsRail } from "@modules/applications/operations";
import { requireGroup } from "./-guards";
import { staffFooterNavFor, staffNavFor } from "./-staff-nav";

// Layout route for the staff tree (/staff/*). Runs the group guard for every child route, then
// wraps them in the app shell — so the sidebar and account card mount once and survive
// navigation between screens instead of remounting per page.
//
// Shared by the administrator AND coordinator surfaces (2026-09-13 — see `SURFACE_GROUP` in
// @core/auth). The observer surface still has no tree, so it never reaches here — the guard
// sends it to the top-level /no-access terminal. Which SCREENS a caller can reach inside this
// shared tree is each route's own `requireSurface`/`requireAnySurface` call, not this layout —
// this guard only proves "is staff", same as it always did.
export const Route = createFileRoute("/staff")({
  beforeLoad: ({ context, location }) =>
    requireGroup(context.queryClient, "staff", location.pathname),
  component: StaffLayout,
});

function StaffLayout() {
  // The guard already resolved (and cached) the profile, so this reads it rather than fetching.
  const { data: profile } = useMe();
  const surfaces = profile?.available_surfaces ?? [];

  // `strict: false` reads whatever the CURRENT match tree carries, without this layout route
  // (which sits above every /staff/* child) needing to name a specific descendant — `roundId`
  // exists only on the round-scoped operations routes (queue/applications/waiting) and is
  // `undefined` everywhere else in the staff tree.
  const { roundId } = useParams({ strict: false });

  return (
    <AppShell
      nav={staffNavFor(
        surfaces,
        roundId ? <RoundOperationsRail roundId={roundId} /> : undefined
      )}
      footerNav={staffFooterNavFor(surfaces)}
    >
      <Outlet />
    </AppShell>
  );
}
